/**
 * Release Property Timeline Page
 *
 * @module features/releases/pages/ReleasePropertyTimeline
 * @description Master-detail timeline view for a property: the timeline on
 * the left, and the selected run's detail (`ReleaseRunPane`) in a pane to
 * the right (stacked below on narrow viewports). Replaces the old separate
 * `/releases/$propertyId/runs/$runId` page — that route now redirects here
 * with `?runId=` (see `router.tsx`).
 *
 * The page header spans the full width; below it the timeline and the pane
 * sit side by side. The pane column is a fixed-width slot at `xl` — reserved
 * whether or not a run is selected — so opening/closing the pane never
 * resizes the timeline beside it (see `docs/ui/decisions.md`: a column that
 * appears/disappears reflows everything next to it). There is no divider:
 * the pane is a borderless white surface the selected band runs straight
 * into (`TimelineRunEntry` cancels the band's right border and extends it
 * across the gutter). Below `xl` the column only takes space when a pane is
 * open, matching the stacked mobile layout. The pane's *content* reveals
 * inside that slot via `PaneReveal` (`ReleaseRunPaneReveal`) — a
 * `clip-path` transition, which cannot affect layout.
 *
 * The pane carries the band's `border-primary` outline, and the 2px
 * `bg-card` "neck" below erases that outline across the selected band's
 * rows (tracked on scroll/resize), so band and pane read as one outlined
 * shape rather than two boxes touching.
 */

import React from 'react';
import { useParams } from '@tanstack/react-router';
import { AnimatePresence } from 'framer-motion';
import { CircleNotch } from '@phosphor-icons/react';
import { Button, cn, Flex, Text, PageHeader } from '@chatsdet/ui';
import { ReleaseTimeline } from '../components/ReleaseTimeline';
import { ReleaseRunPane } from '../components/ReleaseRunPane';
import { ReleaseBuildPane } from '../components/ReleaseBuildPane';
import { buildHeadLabel } from '../buildLabels';
import { relativeBandSlice, type BandRect } from '../components/ReleaseRunPaneReveal';
import { ConfigureReleaseDialog } from '../components/ConfigureReleaseDialog';
import { useReleaseBreadcrumbs } from '../hooks/useReleaseBreadcrumbs';
import { usePropertySummary } from '../hooks/usePropertySummary';
import { isBuildEntry, useReleaseTimeline } from '../hooks/useReleaseTimeline';
import { useLatestDeployment } from '../hooks/useLatestDeployment';
import { useSelectedReleaseRun } from '../hooks/useSelectedReleaseRun';
import { useReleaseAgentAutoOpen } from '../hooks/useReleaseAgentAutoOpen';
import { pickDefaultRunId } from '../releaseRunSelection';
import { useReleaseConfig } from '../useReleases';
import { useAuth } from '../../../contexts/AuthContext';
import { useSandboxV2Applications } from '../../sandboxes/hooks/useSandboxesV2';

export function ReleasePropertyTimeline() {
  const { propertyId } = useParams({ strict: false });
  const { user } = useAuth();
  const [isConfigDialogOpen, setIsConfigDialogOpen] = React.useState(false);
  // The default pane tracks whichever run `pickDefaultRunId` currently picks
  // as newest/live — it opens automatically for that run. Dismissal is
  // per-run-id, local, transient state — never written to the URL — so
  // closing the pane on the default run only suppresses it for that one run
  // id. When a background refetch promotes a *different* run to the default
  // (a new release goes active), the pane — and the live-agent auto-open
  // behind it — comes back for that new run, since dismissal doesn't carry
  // over to it.
  const [dismissedRunId, setDismissedRunId] = React.useState<string | null>(null);
  // The clicked band's own vertical slice, captured at click time (see
  // `TimelineRunEntry`) — purely a reveal-animation hint, never written to
  // the URL. `null` for an auto-opened default/deep-linked run, which falls
  // back to a top-down reveal (see `ReleaseRunPaneReveal`).
  const [originRect, setOriginRect] = React.useState<BandRect | null>(null);
  const columnRef = React.useRef<HTMLDivElement>(null);
  const timelineColumnRef = React.useRef<HTMLDivElement>(null);
  const neckRef = React.useRef<HTMLDivElement>(null);

  const { data: summary, isLoading: summaryLoading } = usePropertySummary(propertyId!);
  const { data: latestDeployment } = useLatestDeployment(propertyId!);
  const { data: configData } = useReleaseConfig(propertyId);
  const {
    data: timelineData,
    isLoading: timelineLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useReleaseTimeline(propertyId!);

  const { selectedRunId: explicitRunId, selectRun } = useSelectedReleaseRun(propertyId!);

  // Flatten pages into single entries array
  const entries = React.useMemo(() => {
    if (!timelineData?.pages) return [];
    return timelineData.pages.flatMap((page) => page.entries);
  }, [timelineData]);

  const defaultRunId = React.useMemo(() => pickDefaultRunId(entries), [entries]);
  const selectedRunId = explicitRunId ?? defaultRunId;
  const isDefaultSelection = !explicitRunId;

  const showPane = Boolean(selectedRunId) && !(isDefaultSelection && dismissedRunId === selectedRunId);

  // A pushed-image application's selection is a build, which has no release run (or agent) behind it. A build not yet loaded (deep link to a later page) shows no pane.
  // Decided from the timeline too: it can arrive before the summary, and a push id must never reach the run/agent hooks.
  const isBuildMode = summary?.mode === 'build' || entries.some(isBuildEntry);
  const selectedBuild = React.useMemo(
    () => entries.filter(isBuildEntry).find((entry) => entry.id === selectedRunId) ?? null,
    [entries, selectedRunId]
  );
  const showBuildPane = showPane && selectedBuild !== null;
  const showRunPane = showPane && !isBuildMode && selectedRunId !== null;

  useReleaseAgentAutoOpen({ runId: showPane && !isBuildMode ? selectedRunId : null });

  useReleaseBreadcrumbs();

  // Positions the neck over the pane's left border across the selected
  // band's rows — written imperatively (no React state) so a scroll never
  // triggers a re-render. The band is found by its `aria-current`, which
  // `TimelineRunEntry` sets on the selected Card. Starts 1px inside the
  // band's top/bottom edges so the pane's border pixel on those rows stays
  // and meets the band's own top/bottom border lines at the corners.
  const updateNeck = React.useCallback(() => {
    const neck = neckRef.current;
    const slot = columnRef.current;
    if (!neck || !slot) return;
    const band = timelineColumnRef.current?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!band) {
      neck.style.height = '0px';
      return;
    }
    const bandRect = band.getBoundingClientRect();
    const slotRect = slot.getBoundingClientRect();
    const top = Math.max(bandRect.top + 1 - slotRect.top, 0);
    const bottom = Math.min(bandRect.bottom - 1 - slotRect.top, slotRect.height);
    neck.style.top = `${top}px`;
    neck.style.height = `${Math.max(bottom - top, 0)}px`;
  }, []);

  React.useLayoutEffect(() => {
    const timelineCol = timelineColumnRef.current;
    if (!timelineCol) return;
    timelineCol.addEventListener('scroll', updateNeck, { passive: true });
    const ro = new ResizeObserver(updateNeck);
    ro.observe(timelineCol);
    if (columnRef.current) ro.observe(columnRef.current);
    updateNeck();
    return () => {
      timelineCol.removeEventListener('scroll', updateNeck);
      ro.disconnect();
    };
  }, [updateNeck, showPane, selectedRunId, entries]);

  // The application's sandbox environments feed the row menus' Claim items. A sandbox application's id is its property's id (`GET /org/sandboxes-v2/applications`).
  const { data: sandboxApplications } = useSandboxV2Applications({ enabled: !!user?.features?.sandboxV2 });
  const claimApplication = sandboxApplications?.find((app) => app.id === propertyId);
  // A commit SHA is a claimable version only for an application connected to GitHub; a pushed-image application's versions are its push ids.
  const claimTarget = React.useMemo(
    () =>
      claimApplication && (claimApplication.delivery === 'upload' || claimApplication.repo !== null)
        ? {
            applicationId: claimApplication.id,
            applicationName: claimApplication.name,
            repo: claimApplication.delivery === 'github' ? claimApplication.repo : null,
            environments: claimApplication.environments.map(({ id, name, environment }) => ({ id, name, environment })),
          }
        : null,
    [claimApplication]
  );

  // Dismiss whatever run is the current default so the pane doesn't immediately re-open on it once `?runId=` clears
  // below — closing an explicitly selected run must actually close the pane, not fall through to the derived default.
  // The click origin belongs to the run just closed, so a later default/deep-linked run must not reveal from it.
  const closePane = () => {
    setDismissedRunId(defaultRunId);
    setOriginRect(null);
    if (explicitRunId) {
      selectRun(null);
    }
  };

  const releaseQaFeatureEnabled = !!user?.features?.releaseQa;
  const config = configData?.config ?? null;
  const suites = configData?.suites ?? [];

  // Compact "Canary Smoke · promote on" style summary of the current config,
  // shown next to Configure so the state is visible without opening the
  // dialog. Only rendered once a config exists.
  const suiteNames = suites.length > 0 ? suites.map((s) => s.name).join(', ') : null;
  const configChipText = !config
    ? null
    : [suiteNames, config.agentMayPromote ? 'promote on' : 'promote off'].filter(Boolean).join(' · ');

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <CircleNotch className="animate-spin text-neutral-400" size={32} />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Text className="text-neutral-500">Application not found or no Release QA config.</Text>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 xl:min-h-0 p-6">
      <PageHeader
        title="Release Timeline"
        description={
          summary.mode === 'build'
            ? buildHeadLabel(summary.head)
            : [
                summary.untestedCommits > 0
                  ? `${summary.untestedCommits} untested commit${summary.untestedCommits !== 1 ? 's' : ''}`
                  : 'All commits tested',
                latestDeployment
                  ? `Deployed at ${latestDeployment.commitSha.slice(0, 7)}`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')
        }
        actions={
          releaseQaFeatureEnabled && config ? (
            <Flex align="center" gap="sm">
              {configChipText && (
                <Text size="sm" className="text-muted-foreground">
                  {configChipText}
                </Text>
              )}
              <Button
                variant="secondary"
                size="sm"
                onPress={() => setIsConfigDialogOpen(true)}
              >
                Configure
              </Button>
            </Flex>
          ) : undefined
        }
      />

      {config && (
        <ConfigureReleaseDialog
          propertyId={propertyId!}
          isOpen={isConfigDialogOpen}
          onOpenChange={setIsConfigDialogOpen}
          existingConfig={config}
          existingRepos={configData?.repos}
          existingSuites={configData?.suites}
        />
      )}

      <div className="mt-6 flex flex-col xl:flex-row flex-1 xl:min-h-0">
        {/* `xl:pr-6` is the gutter the selected band bridges (`xl:-mr-6` in
            `TimelineRunEntry`) to touch the pane. */}
        <div ref={timelineColumnRef} className="flex-1 min-w-0 xl:min-w-[420px] xl:overflow-y-auto xl:pr-6">
          {timelineLoading ? (
            <div className="flex items-center justify-center py-12">
              <CircleNotch className="animate-spin text-neutral-400" size={24} />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12">
              <Text className="text-neutral-500">
                {isBuildMode
                  ? 'No main builds yet. Push an image with the tag main to see it here.'
                  : 'No commits or runs yet. Push commits to your release branch to see them here.'}
              </Text>
            </div>
          ) : (
            <ReleaseTimeline
              entries={entries}
              propertyId={propertyId!}
              deployedSha={latestDeployment?.commitSha}
              hasMore={hasNextPage}
              isLoadingMore={isFetchingNextPage}
              onLoadMore={() => fetchNextPage()}
              repos={configData?.repos}
              claimTarget={claimTarget}
              selectedRunId={showPane ? selectedRunId : null}
              onSelectRun={(runId, rect) => {
                setOriginRect(rect ?? null);
                selectRun(runId);
              }}
            />
          )}
        </div>

        {/* Always reserved at `xl` — a fixed-size slot regardless of whether a
            run is selected, so opening/closing the pane can never resize the
            timeline column beside it. Below `xl`, it only takes space when a
            pane is actually open. Exactly one `ReleaseRunPane` instance ever
            mounts — never two, which would double its data fetch. */}
        <div
          ref={columnRef}
          className={cn(
            // `ReleaseRunPane` positions itself `absolute inset-0` (so a run
            // switch can overlay outgoing/incoming panes without shifting
            // layout — see its module doc), which removes it from flow. Below
            // `xl` that would collapse this wrapper to zero height, so it
            // needs an explicit height there; at `xl` the flex row's stretch
            // sizing already gives it one, matching the timeline column.
            'relative shrink-0 min-h-[480px] xl:min-h-0 mt-6 xl:mt-0',
            'xl:w-[45%] xl:min-w-[480px] xl:max-w-[760px]',
            showBuildPane || showRunPane ? 'block' : 'hidden xl:block'
          )}
        >
          <div
            ref={neckRef}
            aria-hidden="true"
            className="absolute left-[-1px] z-20 w-[2px] bg-card pointer-events-none"
            style={{ top: 0, height: 0 }}
          />
          <AnimatePresence mode="sync">
            {showBuildPane && selectedBuild && (
              <ReleaseBuildPane
                key={selectedBuild.id}
                build={selectedBuild}
                bandSlice={relativeBandSlice(originRect, columnRef.current?.getBoundingClientRect() ?? null)}
                onClose={closePane}
              />
            )}
            {showRunPane && selectedRunId && (
              <ReleaseRunPane
                key={selectedRunId}
                runId={selectedRunId}
                bandSlice={relativeBandSlice(originRect, columnRef.current?.getBoundingClientRect() ?? null)}
                onClose={closePane}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
